import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { MaskedLine, Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import { CASE_STUDIES } from "@/data/caseStudies";

function Block({ label, items }) {
  return (
    <div>
      <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">{label}</p>
      <ul className="border-t border-line">
        {items.map((item) => (
          <li key={item} className="py-3 border-b border-line text-sm text-zinc-300">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const study = CASE_STUDIES.find((c) => c.slug === slug);

  useEffect(() => {
    if (study) document.title = `${study.projectName} — LEDOS Enterprise Solutions`;
  }, [study]);

  if (!study) return <Navigate to="/" replace />;

  const meta = [
    ["Client", study.client],
    ["Industry", study.industry],
    ["Location", study.location],
    ["Number of Locations", study.numLocations],
  ];

  return (
    <main data-testid={`case-study-${study.slug}`}>
      <section className="relative bg-ink pt-44 pb-20 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 grid-bg" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-signal mb-8">
              Case Study
            </p>
          </MaskedLine>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-6xl max-w-4xl">
            <MaskedLine delay={0.25}>{study.projectName}</MaskedLine>
          </h1>
          <Reveal delay={0.4}>
            <dl className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line">
              {meta.map(([k, v]) => (
                <div key={k} className="bg-panel p-5">
                  <dt className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">
                    {k}
                  </dt>
                  <dd className="text-sm text-white">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <SectionHeading index="C." eyebrow="Challenge" title="The Starting Point" />
            <p className="text-base leading-relaxed text-zinc-400">{study.challenge}</p>
            <div className="mt-14">
              <SectionHeading index="S." eyebrow="Solution" title="What We Delivered" />
              <p className="text-base leading-relaxed text-zinc-400">{study.solution}</p>
            </div>
          </div>
          <div className="space-y-12">
            <Block label="Scope of Work" items={study.scopeOfWork} />
            <Block label="Equipment" items={study.equipment} />
            <Block label="Results" items={study.results} />
          </div>
        </div>
        {study.photos?.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 md:px-10 mt-20 grid md:grid-cols-3 gap-6">
            {study.photos.map((src, i) => (
              <div key={i} className="clip-frame relative">
                <img
                  src={src}
                  alt={`${study.projectName} project photo ${i + 1}`}
                  className="w-full h-64 object-cover"
                />
              </div>
            ))}
          </div>
        )}
      </section>

      <CTABand />
    </main>
  );
}
